import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LayoutDashboard, LogOut, RefreshCw } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { API } from "../../config";
import { AdminLogin } from "./AdminLogin";
import { StatsBar } from "./StatsBar";
import { LeadsTable } from "./LeadsTable";
import { StatementsTable } from "./StatementsTable";

const TOKEN_KEY = "cc_admin_token";

export const AdminDashboard = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState(null);
  const [statements, setStatements] = useState(null);
  const [error, setError] = useState("");

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const load = useCallback(
    async (t) => {
      setError("");
      const headers = { Authorization: `Bearer ${t}` };
      try {
        const [l, s] = await Promise.all([
          axios.get(`${API}/leads`, { headers }),
          axios.get(`${API}/admin/statements`, { headers }),
        ]);
        setLeads(l.data);
        setStatements(s.data);
      } catch (e) {
        if (e.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          setError("Couldn't load dashboard data — please try again.");
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  if (!token) {
    return (
      <AdminLogin
        onLogin={(t) => {
          sessionStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  const loading = leads === null || statements === null;

  return (
    <div className="min-h-screen bg-void font-body" data-testid="admin-dashboard">
      <header className="border-b border-ink/10 bg-surface/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-mint-dim">
              <LayoutDashboard className="h-5 w-5 text-mint" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-ink">Lead Dashboard</h1>
              <p className="text-xs text-ink/50">CardConnect Commerce</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-1.5 rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-ink/70 transition-colors hover:border-mint hover:text-mint"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-ink/70 transition-colors hover:border-danger hover:text-danger"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6">
        {error && (
          <p data-testid="admin-load-error" className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </p>
        )}
        {loading ? (
          <p data-testid="admin-loading" className="py-20 text-center text-sm text-ink/45">
            Loading dashboard…
          </p>
        ) : (
          <>
            <StatsBar leads={leads} statements={statements} />
            <LeadsTable leads={leads} />
            <StatementsTable statements={statements} token={token} />
          </>
        )}
      </main>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
};
